// Platform Administrator management (documentation/platform-administrators.md) on the unified
// identity platform: a platform administrator is a person with the platform_admin assignment who
// signs in with any method that satisfies MFA (Microsoft/Google with MFA, or a RadioOS password +
// authenticator app). An administrator invites another one by e-mail; the person accepts the
// one-time invitation (the public link is handled by the invitation page, identityPlatform.ts).
import { apiRequest } from './tenancy';

export type PlatformAdminStatus = 'active' | 'disabled' | 'not_linked';

export interface PlatformAdministrator {
  id: string;
  username: string;
  display_name?: string;
  microsoft_account?: string;
  status: PlatformAdminStatus;
  microsoft_linked: boolean;
  last_login_at?: string;
  created_at: string;
  authorized_by?: string;
  legacy_account: boolean;
  /** Still has bootstrap state (legacy role, a customer/network home or a local password). */
  bootstrap_pending: boolean;
  self: boolean;
  /** How the person can sign in (radioos | microsoft | google). */
  methods?: string[];
}

export interface PlatformInvitation {
  id: string;
  login_name: string;
  invited_by?: string;
  created_at: string;
  expires_at: string;
  expired: boolean;
  delivery_state?: string;
}

export interface PlatformAdminOverview {
  administrators: PlatformAdministrator[];
  invitations: PlatformInvitation[];
  active_count: number;
}

export interface IssuedInvitation {
  invitation: PlatformInvitation & { email?: string };
  delivery_state?: string;
  /** Present only when the invitation was NOT e-mailed: shown once, never stored by the browser. */
  claim_url?: string;
  notice: string;
}

const base = '/api/admin/platform-administrators';
const seg = encodeURIComponent;
const post = (body?: unknown): RequestInit => ({ method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });

export const listPlatformAdmins = () => apiRequest<PlatformAdminOverview>(base);
export const invitePlatformAdmin = (email: string) => apiRequest<IssuedInvitation>(`${base}/invitations`, post({ email }));
export const cancelPlatformInvitation = (id: string) => apiRequest<void>(`${base}/invitations/${seg(id)}`, { method: 'DELETE' });
export const disablePlatformAdmin = (id: string) => apiRequest<void>(`${base}/${seg(id)}/disable`, post());
export const enablePlatformAdmin = (id: string) => apiRequest<void>(`${base}/${seg(id)}/enable`, post());
export const removePlatformAdmin = (id: string) => apiRequest<void>(`${base}/${seg(id)}`, { method: 'DELETE' });
/** Bootstrap finalization: the account becomes a pure platform account (allowed for yourself). */
export const finalizeBootstrapAdmin = (id: string) =>
  apiRequest<{ status: 'finalized' | 'already_finalized'; result: Record<string, unknown> }>(`${base}/${seg(id)}/finalize-bootstrap`, post());
